
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Search,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  Database,
  Plus,
  Trash2,
  Pencil,
  Info,
  Shield,
  Globe,
  Check,
  X,
  BarChart3
} from 'lucide-react';
import { Environment } from '../types';

interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  environments: Environment[];
  activeEnvId: string;
  setActiveEnvId: (id: string) => void;
  addEnvironment: (name: string, isSample?: boolean) => Promise<void>;
  removeEnvironment: (id: string) => Promise<void>;
  renameEnvironment: (id: string, newName: string) => Promise<void>;
}

const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  setIsCollapsed,
  environments,
  activeEnvId,
  setActiveEnvId,
  addEnvironment,
  removeEnvironment,
  renameEnvironment
}) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEnvName, setNewEnvName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');

  const activeEnv = environments.find(e => e.id === activeEnvId);

  const startRename = () => {
    if (!activeEnv || activeEnv.isSample) return;
    setEditNameValue(activeEnv.name);
    setEditingName(true);
  };

  const confirmRename = async () => {
    const trimmed = editNameValue.trim();
    if (trimmed && activeEnvId !== 'none') {
      await renameEnvironment(activeEnvId, trimmed);
    }
    setEditingName(false);
  };

  const navItems = [
    { icon: Shield, label: 'Site Monitor', path: '/seo-monitor' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics' },
    { icon: Search, label: 'SEO Research', path: '/seo' },
    { icon: SettingsIcon, label: 'Settings', path: '/settings' },
  ];

  return (
    <aside className={`${isCollapsed ? 'w-20' : 'w-72'} bg-slate-900 text-white flex flex-col flex-shrink-0 shadow-xl transition-all duration-300 relative`}>
      {/* Collapse Toggle */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-24 bg-blue-500 text-white p-1 rounded-full shadow-lg z-50 hover:scale-110 transition-transform"
      >
        {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      {/* Header Area */}
      <div className={`bg-black/40 p-6 flex flex-col gap-4 ${isCollapsed ? 'items-center px-2' : ''}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex-shrink-0 flex items-center justify-center shadow-lg">
            <Globe size={18} className="text-white" />
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden">
              <h1 className="text-xl font-bold tracking-tight text-white/90 leading-tight">Site Intelligence</h1>
              <p className="text-[10px] text-white/40 uppercase tracking-[0.2em] font-bold">Content Engine</p>
            </div>
          )}
        </div>

        {!isCollapsed && (
          <div className="space-y-4">
            <p className="text-[11px] text-white/50 leading-relaxed italic">
              Select a data environment to begin content monitoring and analysis.
            </p>

            <div className="relative group">
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-[10px] text-white/40 uppercase font-black tracking-widest flex items-center gap-1">
                  Data Environments <Info size={10} className="cursor-help text-white/20 hover:text-blue-400" title="Choose an active workspace or create a new one." />
                </label>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <select
                    value={activeEnvId}
                    onChange={(e) => setActiveEnvId(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2.5 text-xs font-bold text-white focus:ring-1 focus:ring-blue-400 outline-none cursor-pointer appearance-none hover:bg-white/10 transition-all"
                  >
                    <option value="none" className="bg-slate-900">-- Inactive --</option>
                    {environments.map(env => (
                      <option key={env.id} value={env.id} className="bg-slate-900">
                        {env.name} {env.isSample ? '(Sample)' : ''}
                      </option>
                    ))}
                  </select>
                  <Database size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-400" />
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="p-2.5 bg-white/5 border border-white/10 rounded-lg text-blue-400 hover:bg-white/10 transition-all flex-shrink-0"
                  title="New Environment"
                >
                  <Plus size={14} />
                </button>
                {activeEnvId !== 'none' && activeEnv && !activeEnv.isSample && (
                  <>
                    <button
                      onClick={startRename}
                      className="p-2.5 bg-white/5 border border-white/10 rounded-lg text-white/50 hover:text-blue-400 hover:bg-white/10 transition-all flex-shrink-0"
                      title="Rename Environment"
                    >
                      <Pencil size={14} />
                    </button>
                    <button
                      onClick={() => removeEnvironment(activeEnvId)}
                      className="p-2.5 bg-white/5 border border-white/10 rounded-lg text-red-400 hover:bg-red-400/10 transition-all flex-shrink-0"
                      title="Delete Environment"
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>

              {/* Inline rename */}
              {editingName && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    value={editNameValue}
                    onChange={(e) => setEditNameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(); if (e.key === 'Escape') setEditingName(false); }}
                    className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-xs text-white outline-none focus:ring-1 focus:ring-blue-400"
                    autoFocus
                  />
                  <button onClick={confirmRename} className="p-1.5 text-green-400 hover:bg-green-400/10 rounded-lg transition-all" title="Save">
                    <Check size={14} />
                  </button>
                  <button onClick={() => setEditingName(false)} className="p-1.5 text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-all" title="Cancel">
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className={`flex-1 p-4 space-y-1 mt-4 ${isCollapsed ? 'px-2' : ''}`}>
        {!isCollapsed && (
           <label className="text-[10px] text-white/20 uppercase font-black tracking-widest block mb-4 px-4">Workspace</label>
        )}
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) => `
              flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300
              ${isActive
                ? 'bg-blue-500 text-white font-bold shadow-lg shadow-blue-500/10 scale-[1.02]'
                : 'text-white/60 hover:bg-white/5 hover:text-white'
              }
              ${isCollapsed ? 'justify-center px-2' : ''}
            `}
          >
            {({ isActive }) => (
              <>
                <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} />
                {!isCollapsed && <span className="text-sm">{item.label}</span>}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/10 rounded-2xl p-8 w-full max-w-md shadow-2xl">
            <h2 className="text-xl font-bold mb-2">New Data Environment</h2>
            <p className="text-sm text-white/50 mb-6 italic">Create a new workspace for your site.</p>
            <input
              type="text"
              placeholder="Environment Name (e.g. My Website)"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm mb-6 outline-none focus:ring-2 focus:ring-blue-400"
              value={newEnvName}
              onChange={(e) => setNewEnvName(e.target.value)}
              autoFocus
            />
            <div className="flex gap-4">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 py-3 text-sm font-bold text-white/50 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (newEnvName) {
                    await addEnvironment(newEnvName);
                    setNewEnvName('');
                    setShowAddModal(false);
                  }
                }}
                className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-bold hover:scale-[1.02] transition-all"
              >
                Create Workspace
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
